<template>
  <v-row dense>
    <v-col cols="12">
      <v-alert type="info" variant="tonal" density="compact" class="mb-3" icon="mdi-database-check-outline">
        <span class="text-body-2">
          {{ $t('qaSumDbNote') }}
        </span>
      </v-alert>

      <!-- ⚠️ แท็บนี้ต่างจากแท็บอื่นตรงที่ผลลัพธ์ไม่ได้อยู่แค่ในระบบเรา — มันคือข้อความที่
           ขึ้นในฟอร์มปิดงานของเจ้าหน้าที่บน Salesforce · การปิดสวิตช์ที่นี่จึงเป็นการ
           เปลี่ยนสิ่งที่คนทำงานเห็นจริงบนหน้าจอเขา ไม่ใช่แค่หยุดตัวเลขในรายงานของเรา -->
      <v-alert v-if="config.enabled" type="success" variant="tonal" density="compact" class="mb-3" icon="mdi-play-circle-outline">
        <span class="text-body-2">
          {{ $t('qaSumOnBanner', { endpoint: 'GET /api/panel/v1/feedback' }) }}
        </span>
      </v-alert>
      <v-alert v-else type="warning" variant="tonal" density="compact" class="mb-3" icon="mdi-pause-circle-outline">
        <span class="text-body-2">
          {{ $t('qaSumOffBanner') }}
        </span>
      </v-alert>
    </v-col>

    <v-col cols="12" md="7">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaSumPromptTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <v-textarea
            v-model="config.systemPrompt"
            variant="outlined"
            rows="10"
            hide-details
            auto-grow
            spellcheck="false"
            :placeholder="$t('qaEvalPromptPlaceholder')"
          />
          <v-alert type="warning" variant="tonal" density="compact" class="mt-2">
            <span class="text-body-2">
              {{ $t('qaSumPromptAppendNote') }}
            </span>
          </v-alert>
          <div class="text-caption text-medium-emphasis mt-2">
            {{ $t('qaEvalPromptNote') }}
          </div>
        </div>
      </v-card>

      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaSumSectionsTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="text-caption text-medium-emphasis mb-2">
            {{ $t('qaSumSectionsNote') }}
          </div>
          <v-chip v-for="s in sections" :key="s" size="small" variant="tonal" label class="mr-2 mb-2">{{ s }}</v-chip>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12" md="5">
      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalOperation') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSumEnable') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSumEnableDesc') }}
              </div>
            </div>
            <v-switch v-model="config.enabled" color="primary" density="compact" hide-details inset />
          </div>

          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalModel') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSumModelDesc') }}
              </div>
            </div>
            <v-autocomplete
              v-model="config.modelId"
              :items="modelItems"
              item-title="title"
              item-value="value"
              :loading="modelsLoading"
              :error-messages="modelsError"
              density="compact"
              variant="outlined"
              hide-details="auto"
              clearable
              auto-select-first
              prepend-inner-icon="mdi-robot"
              :placeholder="$t('qaEvalModelDefault')"
              style="max-width: 320px"
            >
              <template #item="{ props: itemProps, item }">
                <v-list-item v-bind="itemProps" :subtitle="item.raw.subtitle" />
              </template>
            </v-autocomplete>
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalTimeout') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSumTimeoutDesc') }}
              </div>
            </div>
            <v-text-field v-model.number="config.timeoutSeconds" type="number" min="10" max="180" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
        </div>
      </v-card>

      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaSumCostTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <v-alert type="info" variant="tonal" density="compact" class="mb-3">
            <span class="text-body-2">
              {{ $t('qaSumCostNote') }}
            </span>
          </v-alert>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSumPerRun') }}</div>
              <div class="text-caption text-medium-emphasis">{{ $t('qaSumPerRunDesc') }}</div>
            </div>
            <v-text-field v-model.number="config.maxPerRun" type="number" min="1" max="20" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>

          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSumPerDay') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSumPerDayDesc') }}
              </div>
            </div>
            <v-text-field v-model.number="config.maxPerDay" type="number" min="10" max="5000" density="compact" variant="outlined" hide-details style="max-width: 110px" />
          </div>

          <div class="qa-set-row qa-set-row--stack">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSumBackfill') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSumBackfillDesc') }}
              </div>
            </div>
            <div style="max-width: 320px">
              <v-text-field v-model.number="config.backfillDays" type="number" min="1" max="90" density="compact" variant="outlined" hide-details style="max-width: 100px" />
              <div class="text-caption text-medium-emphasis mt-1">{{ backfillText }}</div>
            </div>
          </div>
        </div>
      </v-card>

      <v-card class="qa-card-sm mb-3" variant="outlined">
        <v-card-title class="py-3"><span class="text-subtitle-1">{{ $t('qaEvalInputTitle') }}</span></v-card-title>
        <v-divider />
        <div class="pa-4">
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaSumMaxLines') }}</div>
              <div class="text-caption text-medium-emphasis">
                {{ $t('qaSumMaxLinesDesc') }}
              </div>
            </div>
            <v-text-field v-model.number="config.maxTranscriptLines" type="number" min="20" max="400" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <div class="qa-set-row">
            <div>
              <div class="text-body-2 font-weight-medium">{{ $t('qaEvalMaxLineLen') }}</div>
            </div>
            <v-text-field v-model.number="config.maxCharsPerLine" type="number" min="50" max="4000" density="compact" variant="outlined" hide-details style="max-width: 100px" />
          </div>
          <v-alert type="info" variant="tonal" density="compact" class="mt-2">
            <span class="text-body-2">
              {{ $t('qaSumMaskNote') }}
            </span>
          </v-alert>
        </div>
      </v-card>
    </v-col>

    <v-col cols="12">
      <v-btn color="primary" variant="flat" :loading="saving" prepend-icon="mdi-content-save-outline" @click="save">{{ $t('qaSetSaveConfig') }}</v-btn>
      <span v-if="updatedAt" class="text-caption text-medium-emphasis ml-3">
        {{ $t('qaSumLastEdited', { when: updatedAtText }) }}<template v-if="updatedBy"> {{ $t('qaSumLastEditedBy', { who: updatedBy }) }}</template>
      </span>
      <span v-else class="text-caption text-medium-emphasis ml-3">{{ $t('qaSumNeverSaved') }}</span>
    </v-col>
  </v-row>
</template>

<script>
/**
 * แท็บตั้งค่า "สรุปการสนทนา" (Q11 · 24 ส.ค. 2026)
 *
 * ⚠️ ผลของแท็บนี้ออกไปนอกระบบเรา — สรุปที่งานเบื้องหลังสร้างคือข้อความที่ขึ้นในช่อง
 *    สรุปของฟอร์มปิดงานบน Salesforce ซึ่งเจ้าหน้าที่แก้แล้วกดส่งกลับเข้า call log
 *    การเปลี่ยนค่าที่นี่จึงเปลี่ยนสิ่งที่คนทำงานเห็นจริง ไม่ใช่แค่ตัวเลขในรายงานของเรา
 *
 * ⚠️ ค่าตั้งต้นของ enabled คือ **เปิด** ต่างจากแท็บ AI Evaluator / อารมณ์ ที่ปิด —
 *    สองแท็บนั้นเป็นงานวิเคราะห์เสริม ปิดไว้แล้วไม่มีใครเดือดร้อน ส่วนแท็บนี้เป็น
 *    ส่วนหนึ่งของขั้นตอนปิดงานจริง ปิดไว้ = ฟอร์มว่างทั้งธนาคาร
 *
 * ⚠️ บันทึกลง cc.QaSettings section 'summary' จริงเสมอ — ห้ามเติมทางสำรองที่เขียนลง
 *    localStorage แล้วขึ้นว่าบันทึกสำเร็จ (เส้นทางแบบนั้นถูกลบไปแล้วทั้งระบบใน Q10)
 */
import qaConsole from '@/services/qaConsole';


export default {
  name: 'QaSummarySettings',
  emits: ['saved'],
  data() {
    return {
      config: {
        enabled: true,
        modelId: null,
        systemPrompt: '',
        timeoutSeconds: 45,
        maxTranscriptLines: 150,
        maxCharsPerLine: 500,
        maxPerRun: 5,
        maxPerDay: 1000,
        backfillDays: 1,
      },
      updatedAt: null,
      updatedBy: null,
      saving: false,
      modelsLoading: false,
      modelsError: '',
      modelItems: [],
    };
  },
  computed: {
    // โครงคงที่ — ตรงกับ QaAiSummary ฝั่ง server และการ์ดบนหน้ารายละเอียดเคส
    // (computed ไม่ใช่ data — $t ใน data() ไม่ reactive ต่อ locale)
    sections() {
      return [
        this.$t('qaSumSecReason'),
        this.$t('qaSumSecKeyPoints'),
        this.$t('qaSumSecActions'),
        this.$t('qaSumSecPromises'),
        this.$t('qaSumSecFollowUp'),
      ];
    },
    updatedAtText() {
      if (!this.updatedAt) return '';
      try {
        return new Date(this.updatedAt).toLocaleString('th-TH');
      } catch {
        return this.updatedAt;
      }
    },
    backfillText() {
      const n = Number(this.config.backfillDays);
      if (!Number.isFinite(n) || n < 1) return '';
      return n === 1
        ? this.$t('qaSumBackfill1')
        : this.$t('qaSumBackfillN', { n });
    },
  },
  mounted() {
    this.load();
    this.loadModels();
  },
  methods: {
    /** หัวรายการ dropdown เลือกโมเดล — เมธอด ไม่ใช่ const ระดับโมดูล (ดู QaEvaluatorSettings) */
    defaultModelItem() {
      return { title: this.$t('qaEvalModelDefaultTitle'), subtitle: this.$t('qaEvalModelDefaultSub'), value: null };
    },
    async load() {
      try {
        const res = await qaConsole.getSummaryConfig();
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaEvalLoadFailed', { msg: err.message }) });
      }
    },

    /**
     * รายการบอทจากหน้า Model Creator — endpoint เดียวกับแท็บอื่น
     * ⚠️ payload ใช้ casing ผสม (r.id / r.name ตัวเล็ก · r.TypeName PascalCase)
     */
    loadModels() {
      const self = this;
      if (!this.$call || typeof this.$call.DDGPTModel_IsPublishByUser !== 'function') return;

      this.modelsLoading = true;
      this.$call.DDGPTModel_IsPublishByUser(
        (dataResponse) => {
          self.modelsLoading = false;
          if (!dataResponse || dataResponse.status !== 0) {
            self.modelsError = self.$t('qaEvalModelsFailed');
            return;
          }
          const rows = dataResponse.data || [];
          self.modelsError = rows.length ? '' : self.$t('qaEvalNoModels');
          self.modelItems = [self.defaultModelItem()].concat(
            rows.map((r) => ({
              title: r.name || `#${r.id}`,
              subtitle: [r.TypeName, `ModelID ${r.id}`].filter(Boolean).join(' · '),
              value: r.id,
            }))
          );
        },
        () => {
          self.modelsLoading = false;
          self.modelsError = self.$t('qaEvalModelsFailed');
        }
      );
    },

    async save() {
      this.saving = true;
      try {
        const res = await qaConsole.saveSummaryConfig(this.config);
        // ⚠️ ใช้ค่าที่ server คืนกลับมา ไม่ใช่ค่าที่พิมพ์ — server บีบค่าที่เกินขอบให้อยู่ในช่วงที่ใช้ได้
        if (res && res.value) this.config = { ...this.config, ...res.value };
        this.updatedAt = res ? res.updatedAt : null;
        this.updatedBy = res ? res.updatedBy : null;
        this.$emit('saved', { ok: true, message: this.$t('qaSumSaved') });
      } catch (err) {
        this.$emit('saved', { ok: false, message: this.$t('qaSetSaveFailed', { msg: err.message }) });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* ⚠️ คัดลอกมาจาก QaSentimentSettings.vue โดยตั้งใจ — คลาสนี้เป็น scoped style ของแต่ละ
   component ไม่ใช่ของกลางใน qa-console.css ถ้าไม่ใส่ แถวตั้งค่าจะกองซ้อนกันเป็นแนวตั้ง */
.qa-set-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f5;
}
.qa-set-row:last-of-type {
  border-bottom: none;
}
.qa-set-row > div:first-child {
  flex: 1;
  min-width: 0;
}
.qa-set-row--stack {
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
}
</style>
